/**
 * Werving → "Stagetypes" (alleen met recht Werving bewerken), naast "Teams & teamleads".
 *
 * Stages komen binnen via de open ingang "Open sollicitatie / stage"; een stagetype is de onderverdeling daarvan
 * (Edit stage, Camera/edit stage, …). De AI deelt nieuwe stages in op deze lijst; de trefwoorden zijn de
 * terugvaloptie als de AI het niet weet. Handmatig ingedeeld blijft altijd staan.
 *
 * Vangnetten: naam, trefwoorden, volgorde, toevoegen, archiveren en de herindeling → ongedaan maken (toast +
 * Ctrl/Cmd+Z, de pagina Vacatures registreert de sneltoets); verwijderen → bevestig (kan alleen zonder sollicitaties,
 * anders bieden we archiveren aan). De herindeling van bestaande stages is eerst een voorstel: er verandert pas iets
 * als je de aangevinkte regels toepast.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, MenuItem, TextField, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import toast from 'react-hot-toast';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { bevestig } from '../../utils/bevestig';
import { wervingApi } from './wervingApi';
import { ACCENT, INKT, LIJN, STIL, dagenGeleden } from './wervingLogica';

const veld = {
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: LIJN },
};

function TypeRij({ t, eerste, bezig, onWijzig, onOmhoog, onArchiveer, onVerwijder }) {
  const [label, setLabel] = useState(t.label);
  const [woorden, setWoorden] = useState((t.trefwoorden || []).join(', '));
  useEffect(() => { setLabel(t.label); setWoorden((t.trefwoorden || []).join(', ')); }, [t]);

  const bewaarLabel = () => {
    const nieuw = label.replace(/\s+/g, ' ').trim();
    if (nieuw === t.label) return;
    if (nieuw.length < 2) { setLabel(t.label); toast.error('Een naam heeft minstens 2 tekens'); return; }
    onWijzig(t, { label: nieuw }, `Naam gewijzigd in ${nieuw}`, { label: t.label }).catch(() => setLabel(t.label));
  };
  const bewaarWoorden = () => {
    const lijst = woorden.split(/[,;\n]+/).map((w) => w.trim().toLowerCase()).filter(Boolean);
    if (lijst.join(',') === (t.trefwoorden || []).join(',')) return;
    onWijzig(t, { trefwoorden: lijst }, `Trefwoorden ${t.label} bijgewerkt`, { trefwoorden: t.trefwoorden || [] })
      .catch(() => setWoorden((t.trefwoorden || []).join(', ')));
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 1.25, borderTop: `1px solid ${LIJN}` }}>
      <Tooltip title={eerste ? '' : 'Omhoog'}>
        <span>
          <IconButton size="small" aria-label={`${t.label} omhoog`} disabled={eerste || bezig} onClick={() => onOmhoog(t)} sx={{ mt: 0.5, color: '#b6bcc4' }}>
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Box sx={{ flex: '0 1 240px', minWidth: 150 }}>
        <TextField size="small" fullWidth value={label} onChange={(e) => setLabel(e.target.value)} onBlur={bewaarLabel}
          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setLabel(t.label); }}
          inputProps={{ 'aria-label': 'Naam stagetype', maxLength: 100 }} sx={{ ...veld, '& input': { fontWeight: 700, color: INKT } }} />
        <Typography sx={{ fontSize: 12, color: STIL, pl: 1.75 }}>
          {t.sollicitaties ? `${t.sollicitaties} sollicitatie${t.sollicitaties === 1 ? '' : 's'}` : 'nog geen sollicitaties'}
        </Typography>
      </Box>
      <TextField size="small" multiline maxRows={3} value={woorden} onChange={(e) => setWoorden(e.target.value)} onBlur={bewaarWoorden}
        placeholder="trefwoorden, gescheiden door komma's" inputProps={{ 'aria-label': `Trefwoorden ${t.label}` }}
        sx={{ flex: '1 1 300px', ...veld, '& textarea': { fontSize: 13, color: STIL } }} />
      <Button size="small" onClick={() => onArchiveer(t, true)} disabled={bezig} sx={{ textTransform: 'none', color: STIL, mt: 0.5 }}>Archiveren</Button>
      <Button size="small" onClick={() => onVerwijder(t)} disabled={bezig} sx={{ textTransform: 'none', color: '#b91c1c', mt: 0.5 }}>Verwijderen…</Button>
    </Box>
  );
}

/** Voorstel voor bestaande stages zonder type: per sollicitatie een keuze (voorgevuld), aanvinken, toepassen. */
function Herindeling({ types, onKlaar }) {
  const [v, setV] = useState(null);
  const [keuze, setKeuze] = useState({});
  const [aan, setAan] = useState({});
  const [bezig, setBezig] = useState(false);

  const laad = async () => {
    setBezig(true);
    try {
      const d = await wervingApi.herindeling();
      setV(d);
      setKeuze(Object.fromEntries(d.voorstellen.map((x) => [x.id, x.voorstel || ''])));
      setAan(Object.fromEntries(d.voorstellen.map((x) => [x.id, !!x.voorstel])));
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };

  const toepassen = async () => {
    const toe = Object.fromEntries(Object.entries(keuze).filter(([id, k]) => aan[id] && k));
    if (!Object.keys(toe).length) { toast.error('Vink eerst minstens één regel aan'); return; }
    setBezig(true);
    try {
      const r = await wervingApi.herindelingToepassen(toe);
      const n = r.gewijzigd.length;
      biedOngedaanAan(`${n} stage${n === 1 ? '' : 's'} ingedeeld`, async () => {
        await wervingApi.herindelingToepassen(Object.fromEntries(Object.entries(r.vorige).map(([id, w]) => [id, w || null])));
        await onKlaar();
      });
      setV(null);
      await onKlaar();
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };

  if (!v) {
    return (
      <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${LIJN}` }}>
        <Typography sx={{ fontWeight: 700, color: INKT, fontSize: 14 }}>Bestaande stages indelen</Typography>
        <Typography sx={{ fontSize: 12.5, color: STIL, mb: 1 }}>
          Een voorstel op basis van de samenvatting van elke stage zonder type. Er verandert niets tot je de aangevinkte regels toepast.
        </Typography>
        <Button size="small" variant="outlined" onClick={laad} disabled={bezig}
          sx={{ textTransform: 'none', color: ACCENT, borderColor: '#cfd6e0' }}>{bezig ? <CircularProgress size={14} /> : 'Voorstel bekijken'}</Button>
      </Box>
    );
  }
  const n = Object.entries(aan).filter(([id, x]) => x && keuze[id]).length;
  return (
    <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${LIJN}` }}>
      <Typography sx={{ fontWeight: 700, color: INKT, fontSize: 14 }}>
        Bestaande stages indelen <Box component="span" sx={{ color: STIL, fontWeight: 400 }}>· {v.totaal} zonder type, {v.met_voorstel} met voorstel</Box>
      </Typography>
      {v.totaal === 0 ? <Typography sx={{ fontSize: 13, color: STIL, mt: 1 }}>Alle stages hebben al een type.</Typography> : (
        <Box sx={{ maxHeight: 360, overflowY: 'auto', mt: 1 }}>
          {v.voorstellen.map((x) => (
            <Box key={x.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderTop: `1px solid ${LIJN}` }}>
              <Checkbox size="small" checked={!!aan[x.id]} onChange={(e) => setAan((a) => ({ ...a, [x.id]: e.target.checked }))}
                inputProps={{ 'aria-label': `${x.naam} indelen` }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, color: INKT }}>{x.naam}</Typography>
                <Typography sx={{ fontSize: 12, color: STIL }}>{dagenGeleden(x.ontvangen_op)}{!x.voorstel && x.kandidaten.length ? ' · twijfel' : ''}</Typography>
              </Box>
              <TextField select size="small" value={keuze[x.id] || ''} sx={{ minWidth: 220 }}
                onChange={(e) => { setKeuze((k) => ({ ...k, [x.id]: e.target.value })); setAan((a) => ({ ...a, [x.id]: !!e.target.value })); }}
                inputProps={{ 'aria-label': `Stagetype ${x.naam}` }}>
                <MenuItem value="">Geen voorstel</MenuItem>
                {types.map((t) => <MenuItem key={t.sleutel} value={t.sleutel}>{t.label}</MenuItem>)}
              </TextField>
            </Box>
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
        <Button size="small" variant="contained" disableElevation onClick={toepassen} disabled={bezig || !n}
          sx={{ textTransform: 'none', fontWeight: 700, bgcolor: ACCENT, '&:hover': { bgcolor: '#434f62' } }}>Toepassen ({n})</Button>
        <Button size="small" onClick={() => setV(null)} sx={{ textTransform: 'none', color: STIL }}>Sluiten</Button>
      </Box>
    </Box>
  );
}

export default function StagetypesBeheer({ open, onClose, onGewijzigd }) {
  const [data, setData] = useState(null);
  const [nieuw, setNieuw] = useState('');
  const [bezig, setBezig] = useState(false);

  const laad = useCallback(() => wervingApi.stagetypes().then(setData).catch((e) => toast.error(e.message)), []);
  useEffect(() => { if (open) { setData(null); laad(); } }, [open, laad]);
  const klaar = async () => { await laad(); onGewijzigd?.(); };
  const lijst = data?.stagetypes || [];
  const actief = lijst.filter((t) => !t.gearchiveerd);
  const archief = lijst.filter((t) => t.gearchiveerd);

  const wijzig = async (t, body, tekst, terug) => {
    setBezig(true);
    try {
      await wervingApi.stagetypeWijzig(t.sleutel, body);
      await klaar();
      biedOngedaanAan(tekst, async () => { await wervingApi.stagetypeWijzig(t.sleutel, terug); await klaar(); });
    } catch (e) { toast.error(e.message); throw e; } finally { setBezig(false); }
  };

  const archiveer = (t, aan) => wijzig(t, { gearchiveerd: aan }, aan ? `${t.label} gearchiveerd` : `${t.label} teruggezet`,
    { gearchiveerd: !aan }).catch(() => {});

  const omhoog = async (t) => {
    const oud = lijst.map((x) => x.sleutel);
    const act = actief.map((x) => x.sleutel);
    const i = act.indexOf(t.sleutel);
    if (i < 1) return;
    [act[i - 1], act[i]] = [act[i], act[i - 1]];
    const volgorde = [...act, ...archief.map((x) => x.sleutel)];
    setBezig(true);
    try {
      await wervingApi.stagetypesVolgorde(volgorde);
      await klaar();
      biedOngedaanAan('Volgorde aangepast', async () => { await wervingApi.stagetypesVolgorde(oud); await klaar(); });
    } catch (e) { toast.error(e.message); await laad(); } finally { setBezig(false); }
  };

  const verwijder = async (t) => {
    if (t.sollicitaties > 0) {
      const ok = await bevestig({
        titel: `${t.label} archiveren?`,
        tekst: `Er ${t.sollicitaties === 1 ? 'hangt 1 sollicitatie' : `hangen ${t.sollicitaties} sollicitaties`} aan ${t.label}, dus verwijderen kan niet. `
          + 'Archiveren haalt het type uit de keuzelijst en uit de AI-indeling; bestaande sollicitaties houden het.',
        knop: 'Archiveren',
      });
      if (ok) await archiveer(t, true);
      return;
    }
    const ok = await bevestig({ titel: `${t.label} verwijderen?`, gevaarlijk: true, knop: 'Verwijderen',
      tekst: `Het stagetype ${t.label} wordt definitief verwijderd. Er hangen geen sollicitaties aan.` });
    if (!ok) return;
    setBezig(true);
    try { await wervingApi.stagetypeVerwijder(t.sleutel); toast.success(`${t.label} verwijderd`); await klaar(); }
    catch (e) { toast.error(e.message); await laad(); } finally { setBezig(false); }
  };

  const voegToe = async () => {
    const label = nieuw.replace(/\s+/g, ' ').trim();
    if (label.length < 2) { toast.error('Geef het stagetype een naam'); return; }
    setBezig(true);
    try {
      const t = await wervingApi.stagetypeMaak({ label });
      setNieuw('');
      await klaar();
      biedOngedaanAan(`${t.label} toegevoegd`, async () => { await wervingApi.stagetypeVerwijder(t.sleutel); await klaar(); });
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ flex: 1 }}>Stagetypes</Box>
        <IconButton aria-label="Sluiten" onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13.5, color: STIL, mb: 2 }}>
          De onderverdeling van stages in het overzicht. Nieuwe stages deelt de AI in op deze lijst; de trefwoorden
          gebruikt hij als hij het niet zeker weet. Wat je zelf indeelt, blijft staan.
          {data?.zonder_type ? ` Nu ${data.zonder_type} stage${data.zonder_type === 1 ? '' : 's'} zonder type.` : ''}
        </Typography>
        {!data ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box> : (
          <Box>
            {actief.map((t, i) => (
              <TypeRij key={t.sleutel} t={t} eerste={i === 0} bezig={bezig} onWijzig={wijzig} onOmhoog={omhoog}
                onArchiveer={archiveer} onVerwijder={verwijder} />
            ))}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', pt: 1.5, borderTop: `1px solid ${LIJN}` }}>
              <TextField size="small" placeholder="Nieuw stagetype, bijv. Sound stage" value={nieuw} onChange={(e) => setNieuw(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') voegToe(); }} sx={{ flex: '0 1 320px' }} inputProps={{ maxLength: 100 }} />
              <Button size="small" variant="outlined" onClick={voegToe} disabled={bezig || nieuw.trim().length < 2}
                sx={{ textTransform: 'none', fontWeight: 600, color: ACCENT, borderColor: '#cfd6e0' }}>Toevoegen</Button>
            </Box>
            {archief.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: 12.5, color: STIL, mb: 0.5 }}>Gearchiveerd</Typography>
                {archief.map((t) => (
                  <Box key={t.sleutel} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderTop: `1px solid ${LIJN}` }}>
                    <Typography sx={{ flex: 1, color: STIL, fontSize: 13.5 }}>{t.label}{t.sollicitaties ? ` · ${t.sollicitaties} sollicitaties` : ''}</Typography>
                    <Button size="small" onClick={() => archiveer(t, false)} disabled={bezig} sx={{ textTransform: 'none', color: ACCENT }}>Terugzetten</Button>
                  </Box>
                ))}
              </Box>
            )}
            <Herindeling types={actief} onKlaar={klaar} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
